import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { posthog } from "../../instrument";
import {
  X,
  ShoppingBag,
  Clock,
  Award,
  ShieldCheck,
  ChevronLeft,
  Plus,
  CheckCircle,
  AlertTriangle,
  Lock,
  Tag,
  Trash2,
} from "lucide-react";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
  formatPrice,
  formatCurrencyAmount,
  calculateTax,
  getCurrentCurrency,
  getCurrentPlatform,
  getPriceWithTax,
  getTaxDisplayText,
  hasTaxNote,
  getAccreditationPrice,
  getCoursePrice,
  PLATFORMS,
} from "../../utils/platform";
import { razorpayService } from "../../services/razorpayService";
import { stripeService } from "../../services/stripeService";
import { isPlatformAdmin } from "../../utils/roles";
import { getDefaultAccreditationType } from "../../utils/courseAccreditation";
import { getOptimizedImageUrl } from "../../utils/imageUtils";
import { getCurrentBranding } from "../../utils/branding";
import { courseService } from "../../services/courseService";
import { enrollmentService } from "../../services/enrollmentService";
import { getDiscountBreakdown } from "../../utils/discountUtils";
import {
  buildPaymentDiscountPayload,
  resolveCartPartnerId,
} from "../../utils/paymentPayloads";
import { supabase } from "../../lib/supabase";
import { teamService } from "../../services/partnerService";
import { bundleService } from "../../services/bundleService";
import { useWhiteLabel } from "../../contexts/WhiteLabelContext";
import {
  getUserPaymentContact,
  validatePaymentContact,
} from "../../utils/paymentContact";
import { describeCartOverlap, findCartOverlaps } from "./cartState";
import { resolveStorePartnerSlug } from "../../utils/storePartnerContext";
import { buildSupportHref, SUPPORT_TOPICS } from "../../utils/supportContext";

// "Check payment status" re-verifies the same Razorpay order; it never opens
// checkout. Bounded so a learner is pointed to support instead of retrying
// forever.
const MAX_PAYMENT_STATUS_CHECKS = 5;

// What the learner is told for each server-reported Razorpay state. None of
// it promises automatic confirmation, and every paid state says not to pay
// again.
const PAYMENT_STATUS_COPY = {
  review: {
    title: "Payment received — under review",
    body: "We received your payment, but it needs a manual check before your course is added. Our team will contact you. Please don't pay again.",
  },
  pending: {
    title: "Payment not confirmed yet",
    body: "We couldn't confirm this payment yet, so your course hasn't been added. Please don't pay again — your bank may still be confirming it. Check the status in a few minutes; if it still isn't confirmed, contact support with the reference below.",
  },
  not_paid: {
    title: "Payment not completed",
    body: "The payment gateway reports this payment wasn't completed, so you haven't been charged. You can add the course to your cart and try again.",
  },
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Store context slug: WhiteLabelContext (subdomain or custom domain), with
// the partner subdomain parsed from the hostname as a fallback.
const getStorePartnerSlug = (whiteLabelSlug) =>
  resolveStorePartnerSlug({
    whiteLabelSlug,
    hostname: window.location.hostname,
    partnerDomain:
      import.meta.env.VITE_PARTNER_DOMAIN || "procertcompliance.com",
  });

// Detect partner_id from the store context
const getStorePartnerId = async (whiteLabelSlug) => {
  const slug = getStorePartnerSlug(whiteLabelSlug);
  if (!slug) return null;
  try {
    const partner = await teamService.getTeamBySlug(slug, {
      logErrors: false,
    });
    return partner?.id || null;
  } catch {
    return null;
  }
};

const extractISOCode = (framework) => {
  if (!framework) return null;
  const match = framework.match(/\d{4,5}/);
  return match ? match[0] : null;
};

const getISOCodesFromCourse = (course) => {
  if (!course?.iso_frameworks || !Array.isArray(course.iso_frameworks))
    return [];
  return course.iso_frameworks.map(extractISOCode).filter(Boolean);
};

const getPlatformCheckoutCurrency = () => {
  const platform = getCurrentPlatform();
  if (platform === PLATFORMS.ISOGYAAN) return "INR";
  if (platform === PLATFORMS.SKILL_COMPLIANCE_CA) return "CAD";
  return "USD";
};

const hasPartnerCheckoutContext = (items, whiteLabelSlug) =>
  Boolean(getStorePartnerSlug(whiteLabelSlug)) ||
  items.some((item) => item?.partner_id);

const CartDrawer = ({ isOpen, onClose, onOpen, allCourses = [] }) => {
  const {
    cartItems,
    removeFromCart,
    getCartTotal,
    addToCart,
    isInCart,
    clearCart,
    discountCode,
    discountInfo,
    applyDiscount,
    removeDiscount,
    refresherPricingPending,
    refresherPricingError,
  } = useCart();
  const { user, userProfile } = useAuth();
  const { isWhiteLabel, partnerSlug } = useWhiteLabel();
  const navigate = useNavigate();
  const [whiteLabelCurrency, setWhiteLabelCurrency] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [step, setStep] = useState("cart");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [removingId, setRemovingId] = useState(null);
  const [fetchedCourses, setFetchedCourses] = useState([]);
  const [discountInput, setDiscountInput] = useState("");
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState("");
  // A Razorpay payment that reached the gateway but is not confirmed:
  // { state: "pending" | "review" | "not_paid", reference, verifyPayload,
  //   checks, checking }. The server decides the state; this only shows it.
  const [paymentStatus, setPaymentStatus] = useState(null);
  const checkoutInFlightRef = useRef(false);
  const panelRef = useRef(null);
  // The store's configured currency, read from the partner record rather than
  // the WorkspaceLayout currency override — the drawer also renders on pages
  // outside that layout, where the override is not set.
  const checkoutCurrency =
    whiteLabelCurrency ||
    (hasPartnerCheckoutContext(cartItems, partnerSlug)
      ? getCurrentCurrency()
      : getPlatformCheckoutCurrency());
  const usesRazorpay = checkoutCurrency === "INR";
  // Whole amounts stay compact; fractional ones (GST, discounts) show the
  // cents actually charged instead of a rounded figure.
  const formatCartPrice = (value) =>
    Number.isInteger(Math.round((Number(value) || 0) * 100) / 100)
      ? formatPrice(value, checkoutCurrency)
      : formatCurrencyAmount(value, checkoutCurrency);

  useEffect(() => {
    if (!isWhiteLabel || !partnerSlug) {
      setWhiteLabelCurrency(null);
      return;
    }
    let cancelled = false;
    teamService
      .getTeamBySlug(partnerSlug, { logErrors: false })
      .then((team) => {
        if (cancelled) return;
        setWhiteLabelCurrency(team?.branding_settings?.currency || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isWhiteLabel, partnerSlug]);

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      document.body.style.overflow = "hidden";
      if (usesRazorpay)
        razorpayService.loadRazorpayScript({ reportError: false });
    } else {
      document.body.style.overflow = "unset";
      setStep("cart");
      setError("");
      setPaymentStatus(null);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, usesRazorpay]);

  // Dialog behaviour: Escape closes, Tab stays inside the panel, and focus
  // returns to whatever opened the drawer.
  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement;
    const focusTimer = setTimeout(() => {
      panelRef.current?.querySelector(FOCUSABLE_SELECTOR)?.focus();
    }, 0);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = [
        ...panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last ||
          !panelRef.current.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (
      isOpen &&
      cartItems.length > 0 &&
      allCourses.length === 0 &&
      fetchedCourses.length === 0
    ) {
      courseService
        .getAllCourses()
        .then((courses) => {
          if (courses?.length) setFetchedCourses(courses);
        })
        .catch(() => {});
    }
  }, [isOpen, cartItems.length, allCourses.length]);

  const availableCourses = allCourses.length > 0 ? allCourses : fetchedCourses;
  // Resolve the effective accreditation type for a cart item, honoring both
  // per-course availability flags. A DB CHECK guarantees at least one is true,
  // so if the remembered choice is disabled we always fall back to the other.
  const effectiveAccreditation = (item) => {
    return getDefaultAccreditationType(item, item?.accreditationType);
  };

  const subtotal = getCartTotal((item) =>
    item.parentCourseData
      ? getCoursePrice(
          item,
          item.parentCourseData,
          effectiveAccreditation(item),
          checkoutCurrency,
        )
      : getAccreditationPrice(
          item,
          effectiveAccreditation(item),
          checkoutCurrency,
        ),
  );
  // Bundles ship with their own negotiated discount via bundle_price_*, so
  // they don't stack with the cart's 4+/5+ quantity-tier discount. The tier
  // threshold is counted against non-bundle items only, and the %-off is
  // applied only to the non-bundle subtotal. Discount-code validation still
  // covers the whole cart at the server.
  const nonBundleItems = cartItems.filter((item) => !item?.isBundle);
  const nonBundleSubtotal = nonBundleItems.reduce((sum, item) => {
    const price = item.parentCourseData
      ? getCoursePrice(
          item,
          item.parentCourseData,
          effectiveAccreditation(item),
          checkoutCurrency,
        )
      : getAccreditationPrice(
          item,
          effectiveAccreditation(item),
          checkoutCurrency,
        );
    return sum + (price || 0);
  }, 0);
  const discountBreakdown = getDiscountBreakdown(
    nonBundleSubtotal,
    nonBundleItems.length,
    discountInfo,
  );
  const discountedSubtotal = Math.max(
    0,
    subtotal - discountBreakdown.totalDiscount,
  );
  const tax = calculateTax(discountedSubtotal, checkoutCurrency);
  const total = discountedSubtotal + tax;

  const suggestedCourses = useMemo(() => {
    if (!cartItems.length || !availableCourses.length) return [];
    const cartISOCodes = new Set();
    const cartCourseIds = new Set(cartItems.map((item) => item.id));
    cartItems.forEach((item) => {
      getISOCodesFromCourse(item).forEach((code) => cartISOCodes.add(code));
    });
    const suggestions = availableCourses.filter((course) => {
      if (cartCourseIds.has(course.id)) return false;
      if (course.isEnrolled) return false;
      if (cartISOCodes.size === 0) return true;
      const courseISOCodes = getISOCodesFromCourse(course);
      return courseISOCodes.some((code) => cartISOCodes.has(code));
    });
    return suggestions.slice(0, 3);
  }, [cartItems, availableCourses]);

  const handleApplyDiscount = async () => {
    if (!discountInput.trim()) {
      setDiscountError("Please enter a discount code");
      return;
    }
    setDiscountLoading(true);
    setDiscountError("");
    try {
      const { data, error } = await supabase.functions.invoke(
        "validate-discount-code",
        {
          body: {
            code: discountInput.trim(),
            userId: user?.id || null,
            partnerId: resolveCartPartnerId({
              hostnamePartnerId: await getStorePartnerId(partnerSlug),
              cartItems,
            }),
            platform: getCurrentPlatform(),
            courseIds: cartItems.filter((c) => !c.isBundle).map((c) => c.id),
            currency: checkoutCurrency,
            cartTotal: subtotal,
          },
        },
      );
      if (error) throw error;
      if (data.valid) {
        posthog.capture("discount_code_applied", {
          discount_type: data.discount?.discountType,
          discount_value: data.discount?.discountValue,
          currency: checkoutCurrency,
          cart_item_count: cartItems.length,
        });
        applyDiscount(discountInput.trim(), {
          discount_id: data.discount?.id,
          discount_type: data.discount?.discountType,
          discount_value: data.discount?.discountValue,
          currency: data.discount?.currency,
          description: data.discount?.description,
        });
        setDiscountInput("");
        setDiscountError("");
      } else {
        setDiscountError(data.error || "Invalid discount code");
      }
    } catch (err) {
      console.error("Discount validation error:", err);
      setDiscountError(err?.message || "Failed to validate discount code");
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    removeDiscount();
    setDiscountError("");
  };

  // One checkout at a time: the pre-checkout lookups below are async, so the
  // button must be locked from the first click, not only once payment starts.
  const handleProceedToCheckout = async () => {
    if (checkoutInFlightRef.current) return;
    checkoutInFlightRef.current = true;
    setLoading(true);
    try {
      await runCheckout();
    } finally {
      checkoutInFlightRef.current = false;
      setLoading(false);
    }
  };

  const runCheckout = async () => {
    if (!user) {
      sessionStorage.setItem("post_login_action", "checkout_cart");
      const returnUrl = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      onClose();
      return;
    }
    if (cartItems.length === 0) return;
    setError("");

    // Refreshers are priced from their parent course; until that price is
    // loaded the cart total would not match what checkout charges.
    if (refresherPricingPending) {
      setError(
        refresherPricingError
          ? "We couldn't load the price for a refresher course in your cart. Remove it and add it again, or try later."
          : "Updating refresher pricing. Please try again in a moment.",
      );
      return;
    }

    // Check enrollment against the DB, not the catalog cache loaded on
    // mount — the user may have enrolled in another tab since then.
    let enrolledCourseIds = [];
    let enrollmentCheckSucceeded = false;
    try {
      const enrollmentVariants =
        await enrollmentService.getUserEnrollmentVariants(user.id);
      enrolledCourseIds = enrollmentVariants.selfPacedCourseIds;
      enrollmentCheckSucceeded = true;
    } catch {
      /* fall back to the cached catalog flags below */
    }
    const enrolledIdSet = new Set(enrolledCourseIds);
    const enrolledInCart = cartItems.filter((item) => {
      if (enrolledIdSet.has(item.id)) return true;
      if (enrollmentCheckSucceeded) return false;
      const freshCourse = availableCourses.find((c) => c.id === item.id);
      return freshCourse?.isSelfPacedEnrolled ?? freshCourse?.isEnrolled;
    });
    if (enrolledInCart.length > 0) {
      if (enrolledInCart.length === cartItems.length) {
        setError(
          cartItems.length === 1
            ? "You are already enrolled in this course. Open My Courses to continue learning."
            : "You are already enrolled in every course in your cart. Open My Courses to continue learning.",
        );
        return;
      }

      enrolledInCart.forEach((item) => removeFromCart(item.id));
    }

    // A bundle hides its courses behind a single cart line, so the check above
    // can't see them. Expand each bundle and block checkout if the learner
    // already owns one of its courses.
    const bundleCartItems = cartItems.filter((item) => item?.isBundle);
    const bundleCoursesById = new Map();
    for (const bundleItem of bundleCartItems) {
      try {
        const bundle = await bundleService.getBundleById(bundleItem.bundleId);
        bundleCoursesById.set(bundleItem.bundleId, bundle?.courses || []);
      } catch {
        // Can't verify client-side; the server re-checks overlap and
        // ownership.
      }
    }

    // A course on its own and inside a bundle (or in two bundles) would be
    // charged twice; the servers reject it with 409.
    const overlaps = findCartOverlaps(
      cartItems.filter(
        (item) => !enrolledInCart.some((owned) => owned.id === item.id),
      ),
      bundleCoursesById,
    );
    if (overlaps.length > 0) {
      setError(describeCartOverlap(overlaps));
      return;
    }

    if (enrolledIdSet.size > 0 && bundleCartItems.length > 0) {
      for (const bundleItem of bundleCartItems) {
        const bundleCourses = bundleCoursesById.get(bundleItem.bundleId);
        if (!bundleCourses) continue;
        const ownedCourse = bundleCourses.find((course) =>
          enrolledIdSet.has(course.id),
        );
        if (ownedCourse) {
          setError(
            `"${bundleItem.title}" includes "${ownedCourse.title}", which you are already enrolled in. Remove the bundle to continue, or open My Courses.`,
          );
          return;
        }
      }
    }

    const checkoutItems = cartItems.filter(
      (item) => !enrolledInCart.some((owned) => owned.id === item.id),
    );
    const checkoutNonBundleItems = checkoutItems.filter(
      (item) => !item?.isBundle,
    );
    const itemPrice = (item) =>
      item.parentCourseData
        ? getCoursePrice(
            item,
            item.parentCourseData,
            effectiveAccreditation(item),
            checkoutCurrency,
          )
        : getAccreditationPrice(
            item,
            effectiveAccreditation(item),
            checkoutCurrency,
          );
    const checkoutSubtotal = checkoutItems.reduce(
      (sum, item) => sum + (itemPrice(item) || 0),
      0,
    );
    const checkoutNonBundleSubtotal = checkoutNonBundleItems.reduce(
      (sum, item) => sum + (itemPrice(item) || 0),
      0,
    );
    const checkoutDiscountBreakdown = getDiscountBreakdown(
      checkoutNonBundleSubtotal,
      checkoutNonBundleItems.length,
      discountInfo,
    );
    const checkoutDiscountedSubtotal = Math.max(
      0,
      checkoutSubtotal - checkoutDiscountBreakdown.totalDiscount,
    );

    posthog.capture("cart_checkout_started", {
      cart_item_count: checkoutItems.length,
      currency: checkoutCurrency,
      subtotal: checkoutSubtotal,
      total: getPriceWithTax(checkoutDiscountedSubtotal, checkoutCurrency),
    });

    try {
      const totalAmount =
        Math.round(
          getPriceWithTax(checkoutDiscountedSubtotal, checkoutCurrency) * 100,
        ) / 100;
      const bundleItems = checkoutItems.filter((item) => item.isBundle);
      const courseItems = checkoutItems.filter((item) => !item.isBundle);
      const courseIds = courseItems.map((c) => c.id);
      const bundleIds = bundleItems.map((b) => b.bundleId);
      const accreditationTypes = Object.fromEntries(
        courseItems.map((c) => [c.id, effectiveAccreditation(c)]),
      );
      const parentCourseIds = Object.fromEntries(
        courseItems
          .filter((c) => c.parentCourseData)
          .map((c) => [c.id, c.parentCourseData.id]),
      );
      const partnerId = resolveCartPartnerId({
        hostnamePartnerId: await getStorePartnerId(partnerSlug),
        cartItems: checkoutItems,
      });
      const discountPayload = buildPaymentDiscountPayload({
        discountCodeId: discountInfo?.discount_id,
        discountCode: discountCode || null,
        discountAmount: checkoutDiscountBreakdown.codeDiscount,
        quantityDiscountAmount: checkoutDiscountBreakdown.quantityDiscount,
        originalAmount: checkoutSubtotal,
      });

      if (totalAmount === 0) {
        if (subtotal !== 0 || bundleItems.length > 0) {
          setError(
            "This cart totals 0, but it cannot be processed through the payment gateway. Please contact support for access.",
          );
          return;
        }

        // Server-side enrollment: the edge function re-validates from the
        // DB that every course is really free before enrolling.
        try {
          await enrollmentService.enrollFreeCourses({
            courseIds,
            accreditationTypes,
            partnerId: partnerId || null,
          });
        } catch (freeError) {
          // Partial failure: keep only the failed courses in the cart so a
          // retry doesn't re-attempt (idempotent but confusing) successes.
          if (freeError?.enrolled?.length > 0) {
            freeError.enrolled.forEach((courseId) => removeFromCart(courseId));
          }
          throw freeError;
        }
        posthog.capture("free_enrollment_completed", {
          course_count: courseIds.length,
        });
        clearCart();
        setStep("success");
        return;
      }

      if (usesRazorpay) {
        const paymentContact = getUserPaymentContact(user, userProfile);
        const contactError = validatePaymentContact(paymentContact, {
          currency: checkoutCurrency,
        });
        if (contactError) {
          setError(`${contactError} Update your profile before paying.`);
          return;
        }

        const orderData = await razorpayService.createOrder({
          user_id: user.id,
          course_ids: courseIds,
          bundle_ids: bundleIds,
          is_cart_purchase: true,
          amount: totalAmount,
          currency: checkoutCurrency,
          accreditationTypes,
          refresherInfo: parentCourseIds,
          partner_id: partnerId || undefined,
          ...discountPayload,
        });

        const scriptLoaded = await razorpayService.loadRazorpayScript();
        if (!scriptLoaded || typeof window.Razorpay !== "function") {
          throw new Error("Payment gateway failed to load. Please try again.");
        }

        const paymentResult = await razorpayService.openCheckout(orderData, {
          description:
            checkoutItems.length === 1
              ? checkoutItems[0].title
              : `${checkoutItems.length} courses`,
          prefill: {
            name: userProfile?.full_name || user?.email,
            email: user?.email,
            phone: paymentContact,
          },
          metadata: {
            user_id: user.id,
          },
        });

        const verifyPayload = {
          razorpay_payment_id: paymentResult.razorpay_payment_id,
          razorpay_order_id: paymentResult.razorpay_order_id,
          razorpay_signature: paymentResult.razorpay_signature,
        };
        let verifyResult;
        try {
          verifyResult = await razorpayService.verifyPayment(verifyPayload);
        } catch (verifyError) {
          // The gateway reports no completed payment: nothing was charged,
          // so keep the cart and let the learner try again.
          if (verifyError?.notPaid) {
            setError(
              "Your payment was not completed, so you have not been charged. Please try again.",
            );
            return;
          }
          // Otherwise the payment may have succeeded at the gateway, so it
          // is shown as not confirmed yet (never as failed).
          console.error("Verify failed after successful payment:", verifyError);
          verifyResult = { success: false, pending: true };
        }

        if (verifyResult?.success !== false) {
          posthog.capture("checkout_completed", {
            payment_method: "razorpay",
            currency: checkoutCurrency,
            cart_item_count: cartItems.length,
            total: totalAmount,
          });
          clearCart();
          setStep("success");
        } else {
          // Not confirmed (capture pending, or verification unavailable) or
          // held for manual review. The money may be taken, so empty the
          // cart: keeping the paid items there invites a second charge for
          // the same courses. Nothing here promises automatic confirmation.
          clearCart();
          setPaymentStatus({
            state: verifyResult?.underReview ? "review" : "pending",
            reference: orderData?.payment_id || null,
            verifyPayload,
            checks: 0,
            checking: false,
          });
          setStep("payment-status");
        }
        return;
      } else {
        const result = await stripeService.createCheckoutSession({
          user_id: user.id,
          courseIds,
          bundleIds,
          totalAmount,
          currency: checkoutCurrency,
          accreditationTypes,
          parentCourseIds,
          partnerId: partnerId || undefined,
          ...discountPayload,
        });
        if (result?.url) {
          posthog.capture("stripe_checkout_initiated", {
            currency: checkoutCurrency,
            cart_item_count: cartItems.length,
            total: totalAmount,
          });
          window.location.href = result.url;
        } else {
          throw new Error("Failed to create checkout session");
        }
      }
    } catch (err) {
      console.error("Checkout error:", err);
      const ownedCourseIds = err?.ownedCourseIds || [];
      if (ownedCourseIds.length > 0) {
        const ownedTitles = ownedCourseIds
          .map(
            (courseId) =>
              cartItems.find((item) => item.id === courseId)?.title ||
              availableCourses.find((course) => course.id === courseId)?.title,
          )
          .filter(Boolean);
        setError(
          ownedTitles.length > 0
            ? `You are already enrolled in ${ownedTitles.join(", ")}. Remove ${
                ownedTitles.length === 1 ? "it" : "them"
              } from your cart — a bundle containing it must be removed as a whole.`
            : "Your cart contains a course you are already enrolled in. Remove it (or the bundle containing it) to continue.",
        );
        return;
      }
      const overlappingCourseIds = err?.overlappingCourseIds || [];
      if (overlappingCourseIds.length > 0) {
        const titles = overlappingCourseIds
          .map(
            (courseId) =>
              cartItems.find((item) => item.id === courseId)?.title ||
              availableCourses.find((course) => course.id === courseId)?.title,
          )
          .filter(Boolean);
        setError(
          titles.length > 0
            ? `${titles.join(", ")} ${titles.length === 1 ? "is" : "are"} in your cart more than once (on its own or in a bundle). Remove the duplicate so you aren't charged twice.`
            : err.message,
        );
        return;
      }
      const msg = err?.message || "";
      if (
        msg.toLowerCase().includes("unauthorized") ||
        msg.includes("401") ||
        msg.toLowerCase().includes("authentication")
      ) {
        setError("Your session has expired. Please sign in again.");
      } else if (msg.includes("gateway") || msg.includes("unavailable")) {
        setError(
          "Payment service is temporarily unavailable. Please try again in a moment.",
        );
      } else {
        setError(msg || "Something went wrong. Please try again.");
      }
    }
  };

  const handleSuccess = () => {
    onClose();
    const isAdmin = isPlatformAdmin(user, userProfile);
    navigate(isAdmin ? "/admin/dashboard" : "/my-courses");
  };

  // Re-asks verify-payment about the same order (one call per click). The
  // server stays authoritative: only its answer moves the status on.
  const handleCheckPaymentStatus = async () => {
    const current = paymentStatus;
    // A review hold is resolved by a person, not by re-verifying (which
    // would only re-log the hold), so it offers no check.
    if (
      !current?.verifyPayload ||
      current.state !== "pending" ||
      current.checking ||
      current.checks >= MAX_PAYMENT_STATUS_CHECKS
    ) {
      return;
    }
    setPaymentStatus((prev) => ({ ...prev, checking: true }));
    let nextState = "pending";
    try {
      const result = await razorpayService.verifyPayment(
        current.verifyPayload,
        { retry: false },
      );
      if (result?.success !== false) nextState = "enrolled";
      else if (result?.underReview) nextState = "review";
    } catch (checkError) {
      if (checkError?.notPaid) nextState = "not_paid";
    }
    if (nextState === "enrolled") {
      setPaymentStatus(null);
      setStep("success");
      return;
    }
    setPaymentStatus((prev) => ({
      ...prev,
      state: nextState,
      checking: false,
      checks: (prev?.checks || 0) + 1,
    }));
  };

  const handlePaymentSupport = () => {
    onClose();
    navigate(
      buildSupportHref({
        topic: SUPPORT_TOPICS.payment,
        reference: paymentStatus?.reference,
      }),
    );
  };

  const handleViewPurchases = () => {
    onClose();
    navigate("/user/purchases");
  };

  const handleBrowseCourses = () => {
    onClose();
    const branding = getCurrentBranding();
    navigate(
      branding?.name === "ProCertCompliance" ? "/courses" : "/course-catalog",
    );
  };

  const handleRemove = (courseId) => {
    setRemovingId(courseId);
    setTimeout(() => {
      removeFromCart(courseId);
      setRemovingId(null);
    }, 200);
  };

  if (!mounted) return null;

  return createPortal(
    <>
      {/* ── Floating Cart Pill ── */}
      <AnimatePresence>
        {cartItems.length > 0 && !isOpen && (
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed right-0 top-1/2 -translate-y-1/2 z-[9999]"
          >
            <motion.button
              onClick={() => onOpen && onOpen()}
              aria-label={`Open cart (${cartItems.length} item${cartItems.length === 1 ? "" : "s"})`}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="group flex items-center bg-dm-text-1 text-dm-page rounded-l-2xl shadow-2xl overflow-hidden transition-all duration-300"
              animate={{ width: isHovered ? "auto" : "48px" }}
            >
              <div className="flex items-center justify-center w-12 h-12 shrink-0 relative">
                <ShoppingBag size={17} strokeWidth={2} />
                <span className="absolute top-1.5 right-1 w-4 h-4 bg-dm-page text-dm-text-1 text-[9px] font-bold rounded-full flex items-center justify-center ring-2 ring-dm-text-1">
                  {cartItems.length}
                </span>
              </div>
              <motion.div
                className="flex items-center gap-2 pr-4 overflow-hidden"
                initial={{ opacity: 0, width: 0 }}
                animate={{
                  opacity: isHovered ? 1 : 0,
                  width: isHovered ? "auto" : 0,
                }}
                transition={{ duration: 0.15 }}
              >
                <div className="text-left whitespace-nowrap">
                  <p className="text-[10px] opacity-60 font-medium">Cart</p>
                  <p className="text-[13px] font-semibold tracking-tight">
                    {formatCartPrice(total)}
                  </p>
                </div>
              </motion.div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Slide-over Drawer ── */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[10000]">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
              className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
            />

            {/* Panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cart-drawer-title"
              className="absolute right-0 top-0 h-full w-full max-w-[420px] bg-dm-surface shadow-2xl flex flex-col border-l border-gray-200/40 dark:border-dm-border"
            >
              {/* ─ Header ─ */}
              <div className="flex items-center justify-between px-5 h-14 border-b border-gray-100 dark:border-dm-border shrink-0">
                <button
                  onClick={onClose}
                  className="flex items-center gap-1 text-[13px] text-dm-text-3 hover:text-dm-text-1 transition-colors -ml-1 font-medium"
                >
                  <ChevronLeft size={16} />
                  Continue Shopping
                </button>
                <span
                  id="cart-drawer-title"
                  className="text-[13px] font-semibold text-dm-text-1 tracking-tight"
                >
                  {step === "success"
                    ? "Order Complete"
                    : step === "payment-status"
                      ? "Payment Status"
                      : `Cart (${cartItems.length})`}
                </span>
                <button
                  onClick={onClose}
                  aria-label="Close cart"
                  className="w-7 h-7 rounded-md flex items-center justify-center text-dm-text-3 hover:text-dm-text-1 hover:bg-dm-hover transition-colors"
                >
                  <X size={15} />
                </button>
              </div>

              {/* ─ Scrollable Content ─ */}
              <div className="flex-1 overflow-y-auto">
                {step === "cart" && cartItems.length === 0 ? (
                  /* ── Empty State ── */
                  <div className="flex flex-col items-center justify-center h-full px-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-dm-overlay flex items-center justify-center mb-5">
                      <ShoppingBag
                        className="text-dm-text-3"
                        size={28}
                        strokeWidth={1.5}
                      />
                    </div>
                    <h3 className="text-base font-semibold text-dm-text-1 mb-1.5">
                      Your cart is empty
                    </h3>
                    <p className="text-[13px] text-dm-text-3 mb-6 leading-relaxed max-w-[260px]">
                      Looks like you haven't added any courses yet. Browse our
                      catalog to find the right one for you.
                    </p>
                    <button
                      onClick={handleBrowseCourses}
                      className="px-6 py-2.5 bg-dm-text-1 text-dm-page text-[13px] font-medium rounded-md hover:opacity-90 transition-all active:scale-[0.98]"
                    >
                      Browse Courses
                    </button>
                  </div>
                ) : step === "cart" ? (
                  <div>
                    {/* ── Cart Items ── */}
                    <AnimatePresence mode="popLayout">
                      {cartItems.map((course, index) => {
                        const effectiveAcc = effectiveAccreditation(course);
                        const price = course.parentCourseData
                          ? getCoursePrice(
                              course,
                              course.parentCourseData,
                              effectiveAcc,
                              checkoutCurrency,
                            )
                          : getAccreditationPrice(
                              course,
                              effectiveAcc,
                              checkoutCurrency,
                            );
                        const isRemoving = removingId === course.id;
                        return (
                          <motion.div
                            key={course.id}
                            layout
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: isRemoving ? 0.3 : 1, y: 0 }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, delay: index * 0.03 }}
                            className="px-5 py-4 border-b border-gray-100/80 dark:border-dm-border/50"
                          >
                            <div className="flex gap-3.5">
                              {/* Thumbnail */}
                              <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-dm-overlay">
                                <img
                                  src={
                                    getOptimizedImageUrl(
                                      course.thumbnail_url,
                                      112,
                                      112,
                                    ) || "/api/placeholder/56/56"
                                  }
                                  alt={course.title}
                                  loading="lazy"
                                  decoding="async"
                                  className="w-full h-full object-cover"
                                />
                              </div>

                              {/* Details */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-[13px] font-semibold text-dm-text-1 leading-snug line-clamp-2 mb-1.5">
                                  {course.title}
                                </h3>
                                <div className="flex items-center gap-1.5 mb-2">
                                  <span
                                    className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                      effectiveAcc === "non_accredited"
                                        ? "bg-dm-overlay text-dm-text-3"
                                        : "bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400"
                                    }`}
                                  >
                                    {effectiveAcc === "non_accredited"
                                      ? "Completion Only"
                                      : "Accredited"}
                                  </span>
                                  {course.duration_hours && (
                                    <span className="text-[10px] text-dm-text-3 flex items-center gap-0.5">
                                      <Clock size={9} /> {course.duration_hours}
                                      h
                                    </span>
                                  )}
                                  {course.credits > 0 && (
                                    <span className="text-[10px] text-dm-text-3 flex items-center gap-0.5">
                                      <Award size={9} /> {course.credits} CEU
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-[13px] font-semibold text-dm-text-1">
                                    {formatCartPrice(price)}
                                  </span>
                                  <button
                                    onClick={() => handleRemove(course.id)}
                                    className="text-[11px] text-dm-text-3 hover:text-red-500 transition-colors font-medium"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>

                    {/* ── Add More ── */}
                    <div className="px-5 py-3">
                      <button
                        onClick={handleBrowseCourses}
                        className="w-full flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-medium text-dm-text-3 hover:text-dm-text-1 transition-colors rounded-lg border border-dashed border-gray-200 dark:border-dm-border hover:border-gray-300 dark:hover:border-dm-text-3 hover:bg-dm-hover"
                      >
                        <Plus size={14} />
                        Add more courses
                      </button>
                    </div>

                    {/* ── Suggestions ── */}
                    {suggestedCourses.length > 0 && (
                      <div className="px-5 py-4 bg-dm-overlay/50 border-t border-gray-100/80 dark:border-dm-border/50">
                        <p className="text-[10px] font-semibold text-dm-text-3 uppercase tracking-wider mb-2.5">
                          You might also like
                        </p>
                        <div className="space-y-2">
                          {suggestedCourses.map((course) => {
                            const inCart = isInCart(course.id);
                            return (
                              <div
                                key={course.id}
                                className="flex items-center gap-3 p-2.5 bg-dm-surface rounded-lg border border-gray-100/60 dark:border-dm-border/40"
                              >
                                <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 bg-dm-overlay">
                                  <img
                                    src={
                                      getOptimizedImageUrl(
                                        course.thumbnail_url,
                                        80,
                                        80,
                                      ) || "/api/placeholder/40/40"
                                    }
                                    alt={course.title}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h5 className="text-[11px] font-semibold text-dm-text-1 line-clamp-1">
                                    {course.title}
                                  </h5>
                                </div>
                                {inCart ? (
                                  <span className="text-[10px] text-dm-text-3 font-medium shrink-0">
                                    Added
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => addToCart(course)}
                                    className="text-[11px] font-medium text-dm-text-1 bg-dm-overlay hover:bg-dm-hover px-2.5 py-1 rounded-md transition-colors shrink-0 flex items-center gap-1"
                                  >
                                    <Plus size={11} /> Add
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : step === "success" ? (
                  /* ── Success ── */
                  <div className="flex flex-col items-center justify-center h-full px-8 text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        damping: 15,
                        stiffness: 200,
                        delay: 0.1,
                      }}
                      className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mb-5"
                    >
                      <CheckCircle
                        className="text-emerald-500"
                        size={28}
                        strokeWidth={1.5}
                      />
                    </motion.div>
                    <motion.h3
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-lg font-semibold text-dm-text-1 mb-1.5"
                    >
                      You're all set!
                    </motion.h3>
                    <motion.p
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="text-[13px] text-dm-text-3 mb-6 leading-relaxed"
                    >
                      Your enrollment is confirmed. Head to your dashboard to
                      start learning.
                    </motion.p>
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="w-full space-y-2.5"
                    >
                      <button
                        onClick={handleSuccess}
                        className="w-full py-2.5 bg-dm-text-1 text-dm-page text-[13px] font-medium rounded-md hover:opacity-90 transition-all active:scale-[0.98]"
                      >
                        Go to Dashboard
                      </button>
                      <button
                        onClick={onClose}
                        className="w-full py-2.5 text-dm-text-3 text-[13px] font-medium hover:text-dm-text-1 transition-colors"
                      >
                        Continue Browsing
                      </button>
                    </motion.div>
                  </div>
                ) : step === "payment-status" && paymentStatus ? (
                  /* ── Payment not confirmed / under review ── */
                  <div
                    role="status"
                    aria-live="polite"
                    className="flex flex-col items-center justify-center h-full px-8 text-center"
                  >
                    <div
                      className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
                        paymentStatus.state === "not_paid"
                          ? "bg-red-50 dark:bg-red-900/20"
                          : "bg-amber-50 dark:bg-amber-900/20"
                      }`}
                    >
                      {paymentStatus.state === "pending" ? (
                        <Clock
                          className="text-amber-500"
                          size={26}
                          strokeWidth={1.5}
                        />
                      ) : (
                        <AlertTriangle
                          className={
                            paymentStatus.state === "not_paid"
                              ? "text-red-500"
                              : "text-amber-500"
                          }
                          size={26}
                          strokeWidth={1.5}
                        />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-dm-text-1 mb-1.5">
                      {PAYMENT_STATUS_COPY[paymentStatus.state]?.title}
                    </h3>
                    <p className="text-[13px] text-dm-text-3 mb-5 leading-relaxed">
                      {PAYMENT_STATUS_COPY[paymentStatus.state]?.body}
                    </p>
                    {paymentStatus.reference && (
                      <div className="w-full mb-5 px-3 py-2.5 rounded-md bg-dm-overlay text-left">
                        <p className="text-[10px] font-semibold text-dm-text-3 uppercase tracking-wider mb-0.5">
                          Purchase reference
                        </p>
                        <p className="font-mono text-[12px] text-dm-text-1 break-all select-all">
                          {paymentStatus.reference}
                        </p>
                      </div>
                    )}
                    {paymentStatus.state === "pending" &&
                      paymentStatus.checks > 0 &&
                      !paymentStatus.checking && (
                        <p className="text-[12px] text-dm-text-2 mb-3 leading-relaxed">
                          {paymentStatus.checks >= MAX_PAYMENT_STATUS_CHECKS
                            ? "Still not confirmed after several checks. Please contact support with the reference above — don't pay again."
                            : "Checked just now: still not confirmed."}
                        </p>
                      )}
                    <div className="w-full space-y-2.5">
                      {paymentStatus.state ===
                      "review" ? null : paymentStatus.state === "not_paid" ? (
                        <button
                          onClick={handleBrowseCourses}
                          className="w-full py-2.5 bg-dm-text-1 text-dm-page text-[13px] font-medium rounded-md hover:opacity-90 transition-all active:scale-[0.98]"
                        >
                          Browse Courses
                        </button>
                      ) : (
                        <button
                          onClick={handleCheckPaymentStatus}
                          disabled={
                            paymentStatus.checking ||
                            paymentStatus.checks >= MAX_PAYMENT_STATUS_CHECKS
                          }
                          className="w-full py-2.5 bg-dm-text-1 text-dm-page text-[13px] font-medium rounded-md hover:opacity-90 transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-40"
                        >
                          {paymentStatus.checking ? (
                            <>
                              <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-dm-page/30 border-t-dm-page" />
                              Checking...
                            </>
                          ) : (
                            "Check payment status"
                          )}
                        </button>
                      )}
                      <button
                        onClick={handlePaymentSupport}
                        className="w-full py-2.5 border border-gray-200 dark:border-dm-border text-dm-text-1 text-[13px] font-medium rounded-md hover:bg-dm-hover transition-colors"
                      >
                        Contact support
                      </button>
                      <button
                        onClick={handleViewPurchases}
                        className="w-full py-2.5 text-dm-text-3 text-[13px] font-medium hover:text-dm-text-1 transition-colors"
                      >
                        View my purchases
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* ─ Sticky Footer ─ */}
              {step === "cart" && cartItems.length > 0 && (
                <div className="border-t border-gray-100 dark:border-dm-border shrink-0 bg-dm-surface">
                  {/* Discount */}
                  <div className="px-5 pt-4 pb-3">
                    {!discountCode ? (
                      <div className="space-y-1.5">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Discount code"
                            value={discountInput}
                            onChange={(e) =>
                              setDiscountInput(e.target.value.toUpperCase())
                            }
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleApplyDiscount()
                            }
                            className="flex-1 px-3 py-2 text-[13px] border border-gray-200 dark:border-dm-border rounded-md bg-dm-surface text-dm-text-1 focus:outline-none focus:ring-1 focus:ring-dm-text-3 focus:border-dm-text-3 placeholder:text-dm-text-3"
                          />
                          <button
                            onClick={handleApplyDiscount}
                            disabled={!discountInput.trim() || discountLoading}
                            className="px-3.5 py-2 text-[12px] font-medium text-dm-text-2 bg-dm-overlay rounded-md hover:bg-dm-hover transition-colors disabled:opacity-40"
                          >
                            {discountLoading ? "..." : "Apply"}
                          </button>
                        </div>
                        {discountError && (
                          <p className="text-[11px] text-red-500">
                            {discountError}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/60 dark:border-emerald-800/40 rounded-md">
                        <div className="flex items-center gap-2">
                          <Tag
                            size={12}
                            className="text-emerald-600 dark:text-emerald-400"
                          />
                          <span className="text-[12px] font-semibold text-emerald-800 dark:text-emerald-300">
                            {discountCode}
                          </span>
                        </div>
                        <button
                          onClick={handleRemoveDiscount}
                          aria-label={`Remove discount code ${discountCode}`}
                          className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 transition-colors"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="px-5 pb-2 space-y-1.5">
                    <div className="flex justify-between text-[12px]">
                      <span className="text-dm-text-3">Subtotal</span>
                      <span className="text-dm-text-1 font-medium">
                        {formatCartPrice(subtotal)}
                      </span>
                    </div>
                    {discountBreakdown.quantityDiscount > 0 && (
                      <div className="flex justify-between text-[12px] text-emerald-600 dark:text-emerald-400">
                        <span className="flex items-center gap-1">
                          <Tag size={10} />
                          {discountBreakdown.quantityDiscountLabel}
                          {cartItems.some((i) => i?.isBundle) && (
                            <span
                              className="text-[10px] text-gray-500 dark:text-dm-text-3"
                              title="Bundles are already discounted and don't count toward the quantity tier."
                            >
                              (excludes bundles)
                            </span>
                          )}
                        </span>
                        <span>
                          -{formatCartPrice(discountBreakdown.quantityDiscount)}
                        </span>
                      </div>
                    )}
                    {discountBreakdown.codeDiscount > 0 && (
                      <div className="flex justify-between text-[12px] text-emerald-600 dark:text-emerald-400">
                        <span className="flex items-center gap-1">
                          <Tag size={10} />
                          Discount ({discountCode})
                        </span>
                        <span>
                          -{formatCartPrice(discountBreakdown.codeDiscount)}
                        </span>
                      </div>
                    )}
                    {tax > 0 && (
                      <div className="flex justify-between text-[12px]">
                        <span className="text-dm-text-3">
                          Tax ({getTaxDisplayText(checkoutCurrency)})
                        </span>
                        <span className="text-dm-text-1 font-medium">
                          {formatCartPrice(tax)}
                        </span>
                      </div>
                    )}
                    {tax === 0 && hasTaxNote(checkoutCurrency) && (
                      <div className="flex justify-between text-[12px]">
                        <span className="text-dm-text-3">Tax</span>
                        <span className="text-dm-text-3">
                          {getTaxDisplayText(checkoutCurrency)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2.5 border-t border-gray-100 dark:border-dm-border">
                      <span className="text-[13px] font-semibold text-dm-text-1">
                        Total
                      </span>
                      <div className="text-right">
                        <span className="text-[15px] font-bold text-dm-text-1">
                          {formatCartPrice(total)}
                        </span>
                        {discountBreakdown.totalDiscount > 0 && (
                          <div className="text-[10px] text-dm-text-3 line-through">
                            {formatCartPrice(subtotal + tax)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="mx-5 mb-2 flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-800/30 text-red-600 dark:text-red-400 rounded-md text-[11px]">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Checkout */}
                  <div className="px-5 pb-4 pt-1.5">
                    <button
                      onClick={handleProceedToCheckout}
                      disabled={cartItems.length === 0 || loading}
                      className="w-full py-3 bg-dm-text-1 text-dm-page text-[13px] font-medium rounded-md hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98]"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-dm-page/30 border-t-dm-page" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Lock size={13} />
                          Checkout
                          <span className="opacity-60 ml-0.5">
                            {formatCartPrice(total)}
                          </span>
                        </>
                      )}
                    </button>
                    <p className="text-center text-[10px] text-dm-text-3 mt-2.5 flex items-center justify-center gap-1">
                      <ShieldCheck size={11} />
                      Secure checkout powered by{" "}
                      {usesRazorpay ? "Razorpay" : "Stripe"}
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>,
    document.body,
  );
};

export default CartDrawer;
